// ─── Mobile strip band settings: the background distribution as data ─────────
//
// The 100px bottom strip's background field — the AI-capabilities /
// takeover-risk distribution rendered behind the capability line — is
// controlled here: both its RENDERING STYLE (halftone vs solid fill, dot
// geometry, color, fringe, edge stroke, opacity) and its RELATIONSHIP TO THE
// CAPABILITY LINE (how far above the line its lower boundary hugs, how thick
// the density ramp is, and which story beat its shape is anchored to).
//
// Edited on /zoom-editor (mobile strip panel), persisted to
// mobileStripBandSettings.json via /api/mobile-strip-band-settings (dev
// only), consumed by MobileCompactHudStrip. These knobs are strip-global —
// unlike the camera keyframes they are not per-plan tracks.

export type MobileStripBandSettings = {
  // ---- rendering style ----
  // "dither" = the halftone dot field; "solid" = flat fills (the graduated
  // fringe rows still render, as stepped solid bands).
  fillStyle: "dither" | "solid"
  // Whole-field opacity (band fills + edge stroke), 0–1.
  bandOpacity: number
  ditherColor: string
  // The halftone texture. Production reference: 2.2px staggered circle grid,
  // dot radius 0.7px (the desktop graph's reference is 4px / 1.6px).
  ditherTilePx: number
  ditherDotRadiusPx: number
  ditherDotShape: "circle" | "square"
  // Offsets alternate dot rows by half a tile — kills the aligned-grid
  // "picnic blanket" read; with dots near pitch width it reads as a solid
  // field that's still visibly dithered.
  ditherStagger: boolean
  // The desktop canvas treatment: dither density is REDUCED around the
  // capability line — it ramps from boundaryFadeMinDensity where the field's
  // lower boundary hugs the line up to full density at the ramp top
  // (bandRampSpanFraction above). The dither renders per dot on a canvas,
  // so the ramp is continuous; boundaryFadeSteps only quantizes the "solid"
  // fill style's stepped-opacity rendering. Off = flat uniform density.
  boundaryFade: boolean
  boundaryFadeSteps: number
  // Density right at the boundary, 0–1 (0 = fully sparse at the line).
  boundaryFadeMinDensity: number
  // The desktop canvas's line-readability treatment, per dot: dither dots
  // shrink as they near the rendered capability line (past + projected
  // future + the current-point dot), so the line stays readable where it
  // climbs through the red. Same formula as the desktop's
  // getPolylineDotRadiusMultiplier: influence = dot radius +
  // lineDotExclusionPaddingPx + the line's half stroke; within it the
  // radius scales linearly down to lineDotRadiusMultiplier at the line
  // (0 = dots vanish at the line; desktop production is 4.5 / 0).
  lineDotExclusionPaddingPx: number
  lineDotRadiusMultiplier: number
  // "band" = the red stops at the block top (bandThicknessFraction of the
  // band); "top" = the red runs all the way to the strip's top edge.
  bandReach: "band" | "top"
  // ("band" reach) the solid block's top edge sits this fraction of the
  // distance from the band's lower boundary toward its upper one (1 = the
  // full band).
  bandThicknessFraction: number
  // ("top" reach) true = solid red above a graduated fringe at the boundary
  // (fringeRows rows of fringeRowHeightPx, density ramping up); false =
  // uniform texture the whole way up.
  bandSolidCore: boolean
  fringeRows: number
  fringeRowHeightPx: number
  // 0 = no edge; >0 strokes the band's lower boundary (the loss-of-control
  // line) as a solid dither-color line.
  bandEdgeStrokeWidthPx: number

  // ---- relationship to the capability line ----
  // The band's lower boundary hugs the capability line this fraction of the
  // CURRENT frame span above it (derived from the old desktop close-up's
  // 20px offset over its 360px plot ≈ 0.0556).
  bandOffsetSpanFraction: number
  // Band max = min + this fraction of the frame span (the density ramp;
  // derived from the old desktop close-up band's 0.69).
  bandRampSpanFraction: number
  // Pre-flop, the field's SHAPE is anchored at this story beat: its boundary
  // hugs the line as of that date, and everything the line does after —
  // the steep projected climb — happens INSIDE the red. Production: 2029
  // (the choice beat).
  bandAnchorYearFraction: number
}

// Production values (the shipped strip-micro-dither look).
export const DEFAULT_MOBILE_STRIP_BAND_SETTINGS: MobileStripBandSettings = {
  fillStyle: "dither",
  bandOpacity: 1,
  ditherColor: "#992424",
  ditherTilePx: 2.2,
  ditherDotRadiusPx: 0.7,
  ditherDotShape: "circle",
  ditherStagger: true,
  boundaryFade: true,
  boundaryFadeSteps: 10,
  boundaryFadeMinDensity: 0,
  // The desktop reference is 4.5; the strip's plot is roughly half the
  // desktop's scale (2.2px tile vs 4px), so the default clears
  // proportionally.
  lineDotExclusionPaddingPx: 3,
  lineDotRadiusMultiplier: 0,
  bandReach: "top",
  bandThicknessFraction: 1,
  bandSolidCore: false,
  fringeRows: 0,
  fringeRowHeightPx: 0,
  bandEdgeStrokeWidthPx: 0,
  bandOffsetSpanFraction: 20 / 360,
  bandRampSpanFraction: 0.69,
  bandAnchorYearFraction: 2029,
}

const sanitizeNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0
    ? value
    : fallback

const sanitizeEnum = <T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: T,
): T => (allowed.includes(value as T) ? (value as T) : fallback)

// Field-wise sanitizer: any missing or malformed field falls back to the
// production default, so a stale or hand-edited JSON can't crash the strip.
export const sanitizeMobileStripBandSettings = (
  raw: unknown,
): MobileStripBandSettings => {
  const defaults = DEFAULT_MOBILE_STRIP_BAND_SETTINGS
  if (typeof raw !== "object" || raw === null) return { ...defaults }
  const candidate = raw as Record<string, unknown>
  return {
    fillStyle: sanitizeEnum(candidate.fillStyle, ["dither", "solid"], defaults.fillStyle),
    bandOpacity: Math.min(1, sanitizeNumber(candidate.bandOpacity, defaults.bandOpacity)),
    ditherColor: typeof candidate.ditherColor === "string" && candidate.ditherColor.length > 0
      ? candidate.ditherColor
      : defaults.ditherColor,
    ditherTilePx: Math.max(0.25, sanitizeNumber(candidate.ditherTilePx, defaults.ditherTilePx)),
    ditherDotRadiusPx: sanitizeNumber(candidate.ditherDotRadiusPx, defaults.ditherDotRadiusPx),
    ditherDotShape: sanitizeEnum(candidate.ditherDotShape, ["circle", "square"], defaults.ditherDotShape),
    ditherStagger: typeof candidate.ditherStagger === "boolean"
      ? candidate.ditherStagger
      : defaults.ditherStagger,
    boundaryFade: typeof candidate.boundaryFade === "boolean"
      ? candidate.boundaryFade
      : defaults.boundaryFade,
    boundaryFadeSteps: Math.max(
      1,
      Math.round(sanitizeNumber(candidate.boundaryFadeSteps, defaults.boundaryFadeSteps)),
    ),
    boundaryFadeMinDensity: Math.min(
      1,
      sanitizeNumber(candidate.boundaryFadeMinDensity, defaults.boundaryFadeMinDensity),
    ),
    lineDotExclusionPaddingPx: sanitizeNumber(
      candidate.lineDotExclusionPaddingPx,
      defaults.lineDotExclusionPaddingPx,
    ),
    lineDotRadiusMultiplier: Math.min(
      1,
      sanitizeNumber(candidate.lineDotRadiusMultiplier, defaults.lineDotRadiusMultiplier),
    ),
    bandReach: sanitizeEnum(candidate.bandReach, ["band", "top"], defaults.bandReach),
    bandThicknessFraction: sanitizeNumber(candidate.bandThicknessFraction, defaults.bandThicknessFraction),
    bandSolidCore: typeof candidate.bandSolidCore === "boolean"
      ? candidate.bandSolidCore
      : defaults.bandSolidCore,
    fringeRows: Math.round(sanitizeNumber(candidate.fringeRows, defaults.fringeRows)),
    fringeRowHeightPx: sanitizeNumber(candidate.fringeRowHeightPx, defaults.fringeRowHeightPx),
    bandEdgeStrokeWidthPx: sanitizeNumber(candidate.bandEdgeStrokeWidthPx, defaults.bandEdgeStrokeWidthPx),
    bandOffsetSpanFraction: sanitizeNumber(candidate.bandOffsetSpanFraction, defaults.bandOffsetSpanFraction),
    bandRampSpanFraction: sanitizeNumber(candidate.bandRampSpanFraction, defaults.bandRampSpanFraction),
    bandAnchorYearFraction: sanitizeNumber(candidate.bandAnchorYearFraction, defaults.bandAnchorYearFraction),
  }
}
